import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Check, Crown, Sparkles, Calendar, Loader2, ShieldCheck } from "lucide-react";

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState({ plans: [], free_signup_looks: 3 });
  const [loadingPlan, setLoadingPlan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setInfo(r.data));
    refresh?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (planId) => {
    if (!window.Razorpay) {
      toast.error("Payment library not loaded. Please refresh the page.");
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: planId });

      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "StyleMe",
        description: `${data.plan_name} subscription`,
        order_id: data.order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        theme: { color: "#E3C2B0" },
        modal: {
          ondismiss: () => {
            setLoadingPlan(null);
            toast("Checkout closed");
          },
        },
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refresh?.();
            navigate(`/subscription/success?session_id=${response.razorpay_order_id}`);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verification failed");
            setLoadingPlan(null);
          }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setLoadingPlan(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
  const daysRemaining = (iso) => (iso ? Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000)) : 0);

  const paidTiers = ["basic", "standard", "premium"];

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 sm:py-16">
        <div className="fade-up max-w-2xl">
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Membership</span>
          <h1 className="font-serif text-4xl sm:text-6xl mt-4 font-light leading-tight">Choose your atelier.</h1>
          <p className="mt-6 text-[#A8A6A1] leading-relaxed">
            Every new member gets {info.free_signup_looks} complimentary looks. Subscribe when you&apos;re ready to compose daily.
          </p>
        </div>

        {/* Status */}
        <div className="mt-12 fade-up delay-100">
          {user?.tier === "free" && user?.is_active && (
            <StatusBar
              icon={<Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />}
              label={`Free member · ${user.free_looks_remaining} of ${info.free_signup_looks} complimentary looks left`}
              detail="No card required"
            />
          )}
          {paidTiers.includes(user?.tier) && (
            <StatusBar
              icon={<Crown className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />}
              label={`${user.tier.charAt(0).toUpperCase() + user.tier.slice(1)} member · ${user.daily_looks_remaining} of ${user.daily_looks_limit} left today · ${daysRemaining(user.subscription_expires_at)} day(s) remaining`}
              detail={`Renews ${formatDate(user.subscription_expires_at)}`}
            />
          )}
          {!user?.is_active && user?.tier === "free" && (
            <StatusBar
              icon={<Calendar className="w-4 h-4 text-red-300" strokeWidth={1.5} />}
              label="Your free looks are used up"
              detail="Subscribe below to keep composing."
              warn
            />
          )}
        </div>

        {/* Plans */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <PlanCard
            tier="free"
            name="Free"
            price={0}
            period="forever"
            features={[
              { label: `${info.free_signup_looks} complimentary looks on signup`, on: true },
              { label: "Full styling breakdown", on: true },
              { label: "Save to gallery", on: true },
              { label: "Affiliate shop links", on: true },
              { label: "Native language translation", on: false },
              { label: "HD preview download", on: false },
            ]}
            isCurrent={user?.tier === "free"}
            cta={null}
          />
          {info.plans.map((plan) => {
            const isPremium = plan.tier === "premium";
            const isStandard = plan.tier === "standard";
            const featuresByTier = {
              basic: [
                { label: "1 AI-styled look per day", on: true },
                { label: "30 looks over 30 days", on: true },
                { label: "Full styling breakdown", on: true },
                { label: "Save to gallery", on: true },
                { label: "Native language translation", on: true },
                { label: "HD preview download", on: true },
                { label: "Priority support", on: false },
              ],
              standard: [
                { label: "3 AI-styled looks per day", on: true },
                { label: "90 looks over 30 days", on: true },
                { label: "Full styling breakdown", on: true },
                { label: "Save to gallery", on: true },
                { label: "Native language translation", on: true },
                { label: "HD preview download", on: true },
                { label: "Priority support", on: true },
              ],
              premium: [
                { label: "Effectively unlimited (999 / month)", on: true },
                { label: "Hairstyle try-on", on: true },
                { label: "Outfit color swap", on: true },
                { label: "Full styling breakdown", on: true },
                { label: "Native language translation", on: true },
                { label: "HD preview download", on: true },
                { label: "Priority support", on: true },
                { label: "7-day money-back guarantee", on: true },
              ],
            };
            return (
              <PlanCard
                key={plan.id}
                tier={plan.tier}
                name={plan.name}
                price={Math.round(plan.amount)}
                period="per month"
                badge={isStandard ? "Most Popular" : (isPremium ? "Best Value" : null)}
                features={featuresByTier[plan.tier] || []}
                isCurrent={user?.tier === plan.tier}
                loading={loadingPlan === plan.id}
                onSelect={() => subscribe(plan.id)}
              />
            );
          })}
        </div>

        <div className="mt-12 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] text-center">
          Secure payments by Razorpay · UPI · Cards · NetBanking · Wallets
        </div>
      </div>
    </div>
  );
}

const StatusBar = ({ icon, label, detail, warn }) => (
  <div className={`flex items-center justify-between flex-wrap gap-3 border ${warn ? "border-red-500/20 bg-red-500/5" : "border-white/10 bg-[#1A1921]"} px-6 py-4 rounded-sm`}>
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-xs uppercase tracking-[0.3em] text-[#F7F5F2]">{label}</span>
    </div>
    <span className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">{detail}</span>
  </div>
);

const PlanCard = ({ tier, name, price, period, features, isCurrent, loading, onSelect, badge, cta }) => {
  const highlight = tier === "premium" || tier === "standard";
  return (
    <div
      data-testid={`plan-${tier}`}
      className={`relative p-8 border ${tier === "premium" ? "border-[#E3C2B0]" : "border-white/10"} bg-[#1A1921] rounded-sm overflow-hidden group hover:-translate-y-1 transition-transform flex flex-col`}
    >
      {badge && (
        <div className="absolute top-0 right-0 bg-[#E3C2B0] text-[#0A0908] text-[10px] uppercase tracking-[0.3em] px-3 py-1.5">
          {badge}
        </div>
      )}
      <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">{name}</span>
      <div className="mt-5 flex items-baseline gap-2">
        <span className="font-serif text-5xl font-light">{price === 0 ? "Free" : `₹${price}`}</span>
        <span className="text-xs text-[#A8A6A1]">/ {period}</span>
      </div>

      <ul className="mt-8 space-y-3 flex-1">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-3 text-sm font-light ${f.on ? "text-[#F7F5F2]/90" : "text-[#A8A6A1]/40 line-through"}`}>
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${f.on ? "text-[#E3C2B0]" : "text-[#A8A6A1]/30"}`} strokeWidth={1.5} />
            <span>{f.label}</span>
          </li>
        ))}
      </ul>

      {cta === null ? (
        <div data-testid={`plan-cta-${tier}`} className={`mt-8 w-full py-3 rounded-sm text-xs uppercase tracking-[0.3em] text-center ${isCurrent ? "bg-white/5 text-[#A8A6A1]" : "bg-white/5 text-[#A8A6A1]"}`}>
          {isCurrent ? "Current Plan" : "Included by default"}
        </div>
      ) : (
        <button
          onClick={onSelect}
          disabled={loading || isCurrent}
          data-testid={`plan-cta-${tier}`}
          className={`mt-8 w-full py-3 rounded-sm text-xs uppercase tracking-[0.3em] transition-colors disabled:opacity-50 flex items-center justify-center gap-3 ${
            tier === "premium"
              ? "bg-[#E3C2B0] text-[#0A0908] hover:bg-[#F0D4C5]"
              : "border border-white/15 text-[#F7F5F2] hover:bg-white/5"
          }`}
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Redirecting...</>
          ) : isCurrent ? "Current Plan" : `Subscribe · ₹${price}`}
        </button>
      )}

      {tier === "premium" && cta !== null && (
        <div data-testid="premium-guarantee" className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#E3C2B0]/80">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>7-day money-back guarantee</span>
        </div>
      )}
    </div>
  );
};
